/**
 * useCategoryValidation Hook
 * 
 * Provides validation functions for categories and groups.
 */

import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

export interface UseCategoryValidationParams {
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  setCategoryNameError: (error: string) => void;
  setCategoryNameWarning: (warning: string) => void;
}

export interface UseCategoryValidationReturn {
  checkCategoryDuplicate: (name: string, groupId: string) => void;
}

/**
 * Hook for category and group validation
 */
export function useCategoryValidation({
  categories,
  groups,
  setCategoryNameError,
  setCategoryNameWarning,
}: UseCategoryValidationParams): UseCategoryValidationReturn {
  // Check for duplicate category names and set error/warning accordingly
  const checkCategoryDuplicate = (name: string, groupId: string): void => {
    const trimmedName = name.trim();
    
    if (!trimmedName) {
      setCategoryNameError("");
      setCategoryNameWarning("");
      return;
    }

    // Find all categories with the same name (case-insensitive)
    const duplicateCategories = categories.filter(
      (cat) => cat.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (duplicateCategories.length === 0) {
      setCategoryNameError("");
      setCategoryNameWarning("");
      return;
    }

    // Check if any duplicate is in the same group
    const sameGroupDuplicate = duplicateCategories.find(
      (cat) => cat.groupId === groupId && groupId !== ""
    );

    if (sameGroupDuplicate && groupId) {
      // Same group - show red error and disable button
      const group = groups.find((g) => g.id === groupId);
      const groupName = group ? group.name : "this group";
      setCategoryNameError(`Category name already exists under group "${groupName}"`);
      setCategoryNameWarning("");
    } else {
      // Different group(s) - show orange warning but allow creation
      setCategoryNameError("");
      const duplicateGroups = duplicateCategories
        .map((cat) => {
          if (!cat.groupId) return null;
          const group = groups.find((g) => g.id === cat.groupId);
          return group ? group.name : null;
        })
        .filter((name): name is string => name !== null);

      if (duplicateGroups.length > 0) {
        const groupsText = duplicateGroups.length === 1 
          ? `group "${duplicateGroups[0]}"`
          : `groups "${duplicateGroups.join('", "')}"`;
        setCategoryNameWarning(`Category with this name already exists under ${groupsText}`);
      } else {
        setCategoryNameWarning("");
      }
    }
  };

  return {
    checkCategoryDuplicate,
  };
}
